import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFotoKegiatan, getFotoKegiatanList, getTodayFotoCount, syncPendingFotoKegiatan } from '../../lib/api';
import { compressImage, formatTime } from '../../lib/utils';
import { addToFotoKegiatanQueue, getPendingFotoKegiatanCount } from '../../lib/db';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Camera, ChevronLeft, Upload, Image, X, 
  Loader2, RotateCcw, AlertCircle, RefreshCw, WifiOff
} from 'lucide-react';
import { toast } from '../ui/sonner';

const FotoKegiatanPage = () => {
  const { user, isOnline } = useAuth();
  const navigate = useNavigate();
  
  // Camera states
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  
  // Form states
  const [keterangan, setKeterangan] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Data states
  const [photoCount, setPhotoCount] = useState({ count: 0, max: 3, can_upload: true });
  const [todayPhotos, setTodayPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCamera, setShowCamera] = useState(false);
  const [pendingOfflineCount, setPendingOfflineCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadData();
    return () => {
      stopCamera();
    };
  }, []);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && pendingOfflineCount > 0) {
      handleSync();
    }
  }, [isOnline]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Always load pending offline count
      const offlineCount = await getPendingFotoKegiatanCount();
      setPendingOfflineCount(offlineCount);

      if (isOnline) {
        // Load server data (don't block sync on this)
        try {
          const [countData, photosData] = await Promise.all([
            getTodayFotoCount(),
            getFotoKegiatanList({ tanggal: new Date().toISOString().split('T')[0] })
          ]);
          setPhotoCount(countData);
          setTodayPhotos(photosData);
        } catch (apiErr) {
          console.error('Failed to load server data:', apiErr);
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncPendingFotoKegiatan();
      if (result.synced > 0) {
        toast.success(`${result.synced} foto kegiatan berhasil disinkronkan!`);
        const newCount = await getPendingFotoKegiatanCount();
        setPendingOfflineCount(newCount);
        // Reload server data
        const [countData, photosData] = await Promise.all([
          getTodayFotoCount(),
          getFotoKegiatanList({ tanggal: new Date().toISOString().split('T')[0] })
        ]);
        setPhotoCount(countData);
        setTodayPhotos(photosData);
      }
      if (result.failed > 0) {
        toast.warning(`${result.failed} foto gagal disinkronkan. Akan dicoba lagi nanti.`);
      }
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncing(false);
    }
  };

  const checkCameraPermission = async () => {
    try {
      // Check if permission API is available
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'camera' });
        console.log('Camera permission status:', result.state);
        return result.state;
      }
      return 'prompt'; // Default to prompt if API not available
    } catch (error) {
      console.log('Permission query not supported:', error);
      return 'prompt';
    }
  };

  // Store pending stream to attach after render
  const pendingStreamRef = useRef(null);

  const initCamera = async () => {
    setCameraError(null);
    setCameraReady(false);
    
    try {
      // Check permission first
      const permissionStatus = await checkCameraPermission();
      
      if (permissionStatus === 'denied') {
        setCameraError('Izin kamera ditolak. Silakan aktifkan izin kamera di pengaturan browser Anda.');
        toast.error('Izin kamera ditolak');
        return;
      }
      
      // Request camera access
      const constraints = {
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 }
        },
        audio: false
      };
      
      console.log('Requesting camera with constraints:', constraints);
      const { getCameraStream } = await import('../../lib/platformUtils');
      const mediaStream = await getCameraStream(constraints);
      console.log('Camera stream obtained:', mediaStream.getVideoTracks());
      
      // Store stream in ref for useEffect to pick up
      pendingStreamRef.current = mediaStream;
      setStream(mediaStream);
      setShowCamera(true);
      
      // Don't try to attach here - let useEffect handle it after render
      console.log('Camera initialized, waiting for video element...');
    } catch (error) {
      console.error('Camera initialization error:', error);
      
      let errorMessage = 'Gagal mengakses kamera.';
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage = 'Izin kamera ditolak. Klik ikon kamera di address bar browser untuk mengizinkan akses.';
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        errorMessage = 'Kamera tidak ditemukan pada perangkat ini.';
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        errorMessage = 'Kamera sedang digunakan oleh aplikasi lain. Tutup aplikasi lain dan coba lagi.';
      } else if (error.name === 'OverconstrainedError') {
        errorMessage = 'Kamera tidak mendukung pengaturan yang diminta. Mencoba pengaturan alternatif...';
        // Try with simpler constraints
        try {
          const { getCameraStream: getSimpleStream } = await import('../../lib/platformUtils');
          const simpleStream = await getSimpleStream({ video: true, audio: false });
          pendingStreamRef.current = simpleStream;
          setStream(simpleStream);
          setShowCamera(true);
          toast.success('Kamera siap (mode sederhana)');
          return;
        } catch (e) {
          errorMessage = 'Gagal mengakses kamera dengan pengaturan alternatif.';
        }
      }
      
      setCameraError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Effect to attach stream to video element AFTER render
  useEffect(() => {
    if (showCamera && pendingStreamRef.current && videoRef.current) {
      const mediaStream = pendingStreamRef.current;
      
      console.log('Attaching stream to video element...');
      videoRef.current.srcObject = mediaStream;
      
      // Setup event handlers
      const videoElement = videoRef.current;
      
      const handleLoadedMetadata = () => {
        console.log('Video metadata loaded, dimensions:', videoElement.videoWidth, 'x', videoElement.videoHeight);
        videoElement.play()
          .then(() => {
            console.log('Video playing successfully');
            setCameraReady(true);
            toast.success('Kamera siap digunakan');
          })
          .catch((err) => {
            console.error('Video play error:', err);
            // Try playing again with user interaction workaround
            setCameraReady(true); // Still allow capture attempt
          });
      };
      
      const handleCanPlay = () => {
        console.log('Video can play');
        if (!videoElement.paused) return;
        videoElement.play().catch(e => console.log('AutoPlay prevented:', e));
      };
      
      const handleError = (err) => {
        console.error('Video element error:', err);
        setCameraError('Terjadi kesalahan pada video');
      };
      
      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.addEventListener('canplay', handleCanPlay);
      videoElement.addEventListener('error', handleError);
      
      // Clear pending ref
      pendingStreamRef.current = null;
      
      return () => {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        videoElement.removeEventListener('canplay', handleCanPlay);
        videoElement.removeEventListener('error', handleError);
      };
    }
  }, [showCamera]);

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop();
        console.log('Stopped track:', track.kind);
      });
      setStream(null);
    }
    setCameraReady(false);
    setShowCamera(false);
  };

  const switchCamera = async () => {
    stopCamera();
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    
    // Re-init camera with new facing mode after short delay
    setTimeout(async () => {
      setCameraError(null);
      setCameraReady(false);
      
      try {
        const constraints = {
          video: {
            facingMode: { ideal: newFacingMode },
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 }
          },
          audio: false
        };
        
        const { getCameraStream: getSwitchStream } = await import('../../lib/platformUtils');
        const mediaStream = await getSwitchStream(constraints);
        pendingStreamRef.current = mediaStream;
        setStream(mediaStream);
        setShowCamera(true);
      } catch (error) {
        console.error('Switch camera error:', error);
        // Fallback to simple video
        try {
          const { getCameraStream: getFallbackStream } = await import('../../lib/platformUtils');
          const simpleStream = await getFallbackStream({ video: true, audio: false });
          pendingStreamRef.current = simpleStream;
          setStream(simpleStream);
          setShowCamera(true);
        } catch (e) {
          setCameraError('Gagal mengganti kamera');
          toast.error('Gagal mengganti kamera');
        }
      }
    }, 300);
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current || !cameraReady) {
      toast.error('Kamera belum siap');
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Make sure video has dimensions
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      toast.error('Video belum siap. Tunggu sebentar dan coba lagi.');
      return;
    }
    
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Get data URL
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    
    if (dataUrl && dataUrl !== 'data:,') {
      setPhoto(dataUrl);
      stopCamera();
      toast.success('Foto berhasil diambil');
    } else {
      toast.error('Gagal mengambil foto. Coba lagi.');
    }
  };

  const retakePhoto = () => {
    setPhoto(null);
    initCamera();
  };

  const handleSubmit = async () => {
    if (!photo) {
      toast.error('Silakan ambil foto terlebih dahulu');
      return;
    }

    setSubmitting(true);
    let compressedPhoto = null;
    try {
      compressedPhoto = await compressImage(photo, 1024, 0.7);

      if (!isOnline) {
        // Save to offline queue
        await addToFotoKegiatanQueue({
          foto_base64: compressedPhoto,
          keterangan: keterangan || '',
        });
        const count = await getPendingFotoKegiatanCount();
        setPendingOfflineCount(count);

        toast.success('Foto disimpan ke antrian offline!', {
          description: `${count} foto menunggu sinkronisasi saat online.`
        });
        setPhoto(null);
        setKeterangan('');
        return;
      }

      await uploadFotoKegiatan({
        foto_base64: compressedPhoto,
        keterangan: keterangan || null
      });

      toast.success('Foto kegiatan berhasil diupload!', {
        description: 'Foto telah tersimpan di server.'
      });
      setPhoto(null);
      setKeterangan('');
      loadData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Gagal mengupload foto';
      
      if (errorDetail.includes('jam') || errorDetail.includes('waktu')) {
        toast.error('Upload Foto Gagal', {
          description: errorDetail,
          duration: 6000
        });
      } else if (errorDetail.includes('kuota') || errorDetail.includes('maksimal')) {
        toast.warning('Kuota Foto Tercapai', {
          description: 'Anda sudah mengupload 3 foto hari ini.'
        });
      } else {
        // If network error, save to offline queue as fallback
        if (!error.response && compressedPhoto) {
          try {
            await addToFotoKegiatanQueue({
              foto_base64: compressedPhoto,
              keterangan: keterangan || '',
            });
            const count = await getPendingFotoKegiatanCount();
            setPendingOfflineCount(count);
            toast.info('Koneksi terputus. Foto disimpan ke antrian offline.', {
              description: `${count} foto menunggu sinkronisasi.`
            });
            setPhoto(null);
            setKeterangan('');
            return;
          } catch (queueErr) {
            console.error('Failed to queue offline:', queueErr);
          }
        }
        toast.error('Upload Gagal', { description: errorDetail });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto" data-testid="foto-kegiatan-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-indigo-950">Foto Kegiatan Mengajar</h1>
          <p className="text-gray-500">Upload foto kegiatan mengajar hari ini</p>
        </div>
      </div>

      {/* Offline Queue Banner */}
      {pendingOfflineCount > 0 && (
        <Alert className="mb-4 border-amber-200 bg-amber-50" data-testid="offline-queue-banner">
          <WifiOff className="w-4 h-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-amber-800">
              <strong>{pendingOfflineCount} foto</strong> menunggu sinkronisasi
            </span>
            {isOnline && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSync}
                disabled={syncing}
                className="border-amber-300 text-amber-700 ml-2"
                data-testid="sync-photos-btn"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sinkronisasi...' : 'Sinkronkan'}
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {!isOnline && (
        <Alert className="mb-4 border-blue-200 bg-blue-50" data-testid="offline-mode-banner">
          <WifiOff className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            Mode Offline — Foto akan disimpan di perangkat dan diupload otomatis saat online.
          </AlertDescription>
        </Alert>
      )}

      {/* Upload Status Card */}
      <Card className="mb-6 border-indigo-100/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                photoCount.count >= 3 ? 'bg-emerald-100' : 'bg-amber-100'
              }`}>
                <Camera className={`w-6 h-6 ${
                  photoCount.count >= 3 ? 'text-emerald-600' : 'text-amber-600'
                }`} />
              </div>
              <div>
                <p className="font-semibold text-indigo-950">
                  {photoCount.count} / {photoCount.max} Foto Hari Ini
                </p>
                <p className="text-sm text-gray-500">
                  {photoCount.can_upload 
                    ? `Anda dapat mengupload ${Math.max(0, photoCount.max - photoCount.count)} foto lagi`
                    : 'Kuota foto hari ini sudah terpenuhi'}
                </p>
              </div>
            </div>
            {photoCount.count >= 3 && (
              <Badge className="bg-emerald-100 text-emerald-800">Lengkap</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Camera/Upload Section */}
      {photoCount.can_upload && (
        <Card className="mb-6 border-indigo-100/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Ambil Foto Kegiatan
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Camera Error Alert */}
            {cameraError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{cameraError}</AlertDescription>
              </Alert>
            )}
            
            {!showCamera && !photo ? (
              <Button
                onClick={initCamera}
                className="w-full h-32 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-2 border-dashed border-indigo-200"
                data-testid="open-camera-btn"
              >
                <div className="text-center">
                  <Camera className="w-10 h-10 mx-auto mb-2" />
                  <p className="font-medium">Klik untuk membuka kamera</p>
                  <p className="text-sm text-indigo-500 mt-1">Izinkan akses kamera saat diminta</p>
                </div>
              </Button>
            ) : showCamera ? (
              <div className="space-y-4">
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                  />
                  
                  {/* Loading overlay */}
                  {!cameraReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                      <div className="text-center text-white">
                        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-2" />
                        <p className="text-sm">Memuat kamera...</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Camera switch button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 bg-black/50 text-white hover:bg-black/70"
                    onClick={switchCamera}
                  >
                    <RefreshCw className="w-5 h-5" />
                  </Button>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={stopCamera}
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Batal
                  </Button>
                  <Button
                    onClick={takePhoto}
                    disabled={!cameraReady}
                    className="flex-1 bg-indigo-950 disabled:opacity-50"
                    data-testid="capture-photo-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {cameraReady ? 'Ambil Foto' : 'Memuat...'}
                  </Button>
                </div>
              </div>
            ) : photo ? (
              <div className="space-y-4">
                <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src={photo} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error('Image preview error');
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="keterangan">Keterangan (Opsional)</Label>
                  <Textarea
                    id="keterangan"
                    value={keterangan}
                    onChange={(e) => setKeterangan(e.target.value)}
                    placeholder="Contoh: Kegiatan belajar Matematika kelas 5A"
                    rows={2}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={retakePhoto}
                    disabled={submitting}
                    className="flex-1"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Ambil Ulang
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="submit-photo-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : null}
            <canvas ref={canvasRef} className="hidden" />
          </CardContent>
        </Card>
      )}

      {/* Today's Photos */}
      <Card className="border-indigo-100/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            Foto Kegiatan Hari Ini
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : todayPhotos.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Image className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Belum ada foto kegiatan hari ini</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {todayPhotos.map((foto, idx) => (
                <div key={foto.id} className="relative group">
                  <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                    <img 
                      src={foto.foto_url} 
                      alt={`Kegiatan ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12">Gambar tidak tersedia</text></svg>';
                      }}
                    />
                  </div>
                  <div className="mt-2">
                    <p className="text-sm font-medium text-indigo-950">
                      Foto #{idx + 1}
                    </p>
                    {foto.keterangan && (
                      <p className="text-xs text-gray-500 truncate">{foto.keterangan}</p>
                    )}
                    <p className="text-xs text-gray-400">{formatTime(foto.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Alert */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Ketentuan Foto Kegiatan:</p>
            <ul className="mt-1 list-disc list-inside space-y-1 text-blue-700">
              <li>Maksimal 3 foto per hari</li>
              <li>Foto harus diambil setelah absen masuk</li>
              <li>Foto menunjukkan kegiatan mengajar di kelas</li>
              <li>Jika kamera tidak muncul, refresh halaman dan izinkan akses kamera</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FotoKegiatanPage;
